import {
  ChartBarIcon as ChartBarIconOutline,
  TableIcon as TableIconOutline,
} from "@heroicons/react/outline";
import {
  ChartBarIcon as ChartBarIconSolid,
  TableIcon as TableIconSolid,
} from "@heroicons/react/solid";
import { observer } from "mobx-react-lite";
import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useAnalyticsQuery } from "./analytics-query-context";
import { ANALYTICS_TAB_INDEX, REQUESTS_TAB_INDEX } from "./constants";
import { getAnalyticsDisabledTooltip } from "./utils/getAnalyticsDisabledTooltip";

import LoadingSpinner from "@/components/LoadingSpinner";
import { useStructuredSearchStore } from "@/components/StructuredSearch";
import { isOlderDataSearch } from "@/routes/request-logs/search/older-data";
import type { Filter } from "@/components/StructuredSearch/config/types";
import type { CompleteDateRange } from "@/components/StructuredSearch/stores/structured-search/structured-search-store";
import TabNavigation from "@/components/ui/TabNavigation";
import { useUser } from "@/context/user-context";
import { trackRequestsAnalyticsEvent } from "@/utils/requests-analytics-tracking";

const AnalyticsContentLazy = lazy(() => import("./AnalyticsContent"));
const RequestLogsAgGridLazy = lazy(() => import("@/routes/analytics/RequestLogsAgGrid"));

const tabPanelSuspenseFallback = (
  <div className="flex h-full min-h-[200px] items-center justify-center">
    <LoadingSpinner />
  </div>
);

const getTabRoute = (tabIndex: number) => {
  if (tabIndex === ANALYTICS_TAB_INDEX) {
    return "analytics";
  }
  return "request-logs";
};

const getTabTrackingName = (tabIndex: number) => {
  if (tabIndex === ANALYTICS_TAB_INDEX) {
    return "Analytics";
  }
  return "Requests";
};

const getAnalyticsDisabledTooltipContent = ({
  isDisabledForOlderData,
  searchCutoverDate,
}: {
  isDisabledForOlderData: boolean;
  searchCutoverDate?: string;
}) => {
  if (isDisabledForOlderData) {
    return getAnalyticsDisabledTooltip(searchCutoverDate);
  }

  return undefined;
};

const RequestsAnalyticsTabs = observer(
  ({
    selectedTabIndex,
    lockedFilters,
    initialDateRange,
    syncUrl = true,
    onSelectedTabChange,
  }: {
    selectedTabIndex: number;
    lockedFilters?: Filter[];
    initialDateRange?: CompleteDateRange;
    syncUrl?: boolean;
    onSelectedTabChange?: (tabIndex: number) => void;
  }) => {
    const structuredSearchStore = useStructuredSearchStore();
    const { setIsSearchEndpointRunning } = useAnalyticsQuery();
    const { activeWorkspace } = useUser();
    const { workspaceId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [localSelectedTabIndex, setLocalSelectedTabIndex] = useState(selectedTabIndex);
    const isAnalyticsDisabledForOlderData =
      !!activeWorkspace &&
      isOlderDataSearch(structuredSearchStore.dateRange, activeWorkspace.search_cutover_date);
    const requestedSelectedTabIndex = syncUrl ? selectedTabIndex : localSelectedTabIndex;
    const currentSelectedTabIndex =
      isAnalyticsDisabledForOlderData && requestedSelectedTabIndex === ANALYTICS_TAB_INDEX
        ? REQUESTS_TAB_INDEX
        : requestedSelectedTabIndex;
    const analyticsDisabledTooltip = getAnalyticsDisabledTooltipContent({
      isDisabledForOlderData: isAnalyticsDisabledForOlderData,
      searchCutoverDate: activeWorkspace?.search_cutover_date,
    });

    const getTabPath = useCallback(
      (tabIndex: number) => {
        if (!syncUrl) {
          return undefined;
        }

        const route = getTabRoute(tabIndex);
        return `/workspace/${workspaceId}/${route}${location.search}`;
      },
      [location.search, syncUrl, workspaceId],
    );

    useEffect(() => {
      if (selectedTabIndex === ANALYTICS_TAB_INDEX && isAnalyticsDisabledForOlderData) {
        if (!syncUrl) {
          setLocalSelectedTabIndex(REQUESTS_TAB_INDEX);
          onSelectedTabChange?.(REQUESTS_TAB_INDEX);
          return;
        }

        const requestLogsPath = getTabPath(REQUESTS_TAB_INDEX);
        if (requestLogsPath) {
          navigate(requestLogsPath, { replace: true });
        }
      }
    }, [
      getTabPath,
      isAnalyticsDisabledForOlderData,
      navigate,
      onSelectedTabChange,
      selectedTabIndex,
      syncUrl,
    ]);

    useEffect(() => {
      setLocalSelectedTabIndex(selectedTabIndex);
    }, [selectedTabIndex]);

    const handleTrackedTabChange = (tabIndex: number) => {
      if (tabIndex !== currentSelectedTabIndex) {
        trackRequestsAnalyticsEvent("Requests Analytics Tab Selected", {
          page: "Requests & Analytics",
          selected_tab: getTabTrackingName(tabIndex),
          previous_tab: getTabTrackingName(currentSelectedTabIndex),
          analytics_disabled_for_older_data: isAnalyticsDisabledForOlderData,
        });
      }

      if (!syncUrl) {
        setLocalSelectedTabIndex(tabIndex);
        onSelectedTabChange?.(tabIndex);
      }
    };

    return (
      <div className="min-h-0 flex-1">
        <TabNavigation
          selectedIndex={currentSelectedTabIndex}
          handleTabChange={handleTrackedTabChange}
          panelClassName="min-h-0"
          panelContainerClassName="mt-3 min-h-0 sm:mt-4"
          tabs={[
            {
              id: REQUESTS_TAB_INDEX,
              name: "Requests",
              path: getTabPath(REQUESTS_TAB_INDEX),
              outlineIcon: <TableIconOutline className="h-4 w-4" />,
              solidIcon: <TableIconSolid className="h-4 w-4" />,
              content:
                currentSelectedTabIndex === REQUESTS_TAB_INDEX ? (
                  <Suspense fallback={tabPanelSuspenseFallback}>
                    <RequestLogsAgGridLazy
                      lockedFilters={lockedFilters}
                      initialDateRange={initialDateRange}
                      showSearchbar={false}
                      syncUrl={false}
                      useExternalStructuredSearchProvider
                      trackingSurface="requests_analytics"
                      onLoadingChange={setIsSearchEndpointRunning}
                    />
                  </Suspense>
                ) : null,
            },
            {
              id: ANALYTICS_TAB_INDEX,
              name: "Analytics",
              path: getTabPath(ANALYTICS_TAB_INDEX),
              outlineIcon: <ChartBarIconOutline className="h-4 w-4" />,
              solidIcon: <ChartBarIconSolid className="h-4 w-4" />,
              content:
                currentSelectedTabIndex === ANALYTICS_TAB_INDEX ? (
                  <Suspense fallback={tabPanelSuspenseFallback}>
                    <AnalyticsContentLazy />
                  </Suspense>
                ) : null,
              disabled: isAnalyticsDisabledForOlderData,
              tooltipContent: analyticsDisabledTooltip,
            },
          ]}
        />
      </div>
    );
  },
);

export default RequestsAnalyticsTabs;
